import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Plus, PencilSimple, Trash, UploadSimple, Star } from "@phosphor-icons/react";

const emptyForm = { name: "", role: "", location: "", quote: "", avatar: "", rating: 5 };

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/testimonials").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, role: t.role || "", location: t.location || "", quote: t.quote, avatar: t.avatar, rating: t.rating || 5 });
    setOpen(true);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}` }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/admin/testimonials/${editingId}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success(editingId ? "Testimonial updated" : "Testimonial added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimonial removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const canSave = form.name && form.quote && form.avatar;

  return (
    <div data-testid="testimonials-manager">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#71717A]">Manage the quotes shown in the homepage Testimonials section.</p>
        <Button data-testid="add-testimonial-button" onClick={openCreate} className="bg-[#D4AF37] text-black hover:bg-white rounded-full shrink-0">
          <Plus size={16} /> Add Testimonial
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-item-${t.id}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex gap-4">
            <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{t.name}</p>
              <p className="text-xs text-[#71717A] mb-2">{t.role}{t.role && t.location ? " \u00b7 " : ""}{t.location}</p>
              <p className="text-sm text-[#D4D4D8] line-clamp-2">&ldquo;{t.quote}&rdquo;</p>
              <div className="flex items-center gap-1 mt-2">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} size={12} weight="fill" className="text-[#D4AF37]" />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid={`edit-testimonial-${t.id}`} onClick={() => openEdit(t)} className="text-[#71717A] hover:text-white transition-colors">
                <PencilSimple size={16} />
              </button>
              <button data-testid={`delete-testimonial-${t.id}`} onClick={() => remove(t.id)} className="text-[#71717A] hover:text-red-400 transition-colors">
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-[#71717A]">No testimonials yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="testimonial-dialog" className="bg-[#141414] border-[#27272A] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">{editingId ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="testimonial-name-input" placeholder="Client Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="testimonial-role-input" placeholder="Role (e.g. Marketing Director)" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
              <input data-testid="testimonial-location-input" placeholder="Location" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            </div>
            <textarea data-testid="testimonial-quote-input" placeholder="Quote" rows={3} value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
            <select data-testid="testimonial-rating-select" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white">
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={r}>{r} Stars</option>
              ))}
            </select>
            <div className="flex items-center gap-3">
              <input data-testid="testimonial-avatar-input" placeholder="Avatar image URL" value={form.avatar} onChange={(e) => setForm((f) => ({ ...f, avatar: e.target.value }))} className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white" />
              <label data-testid="testimonial-avatar-upload-label" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs px-4 py-2.5 rounded-full cursor-pointer transition-colors shrink-0">
                <UploadSimple size={14} /> {uploading ? "..." : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
              </label>
            </div>
            {form.avatar && <img src={form.avatar} alt="preview" className="w-16 h-16 rounded-full object-cover" />}
            <Button data-testid="testimonial-save-button" disabled={!canSave || saving} onClick={save} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-full">
              {saving ? "Saving..." : editingId ? "Save Changes" : "Add Testimonial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
